import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ROLE_LABELS, formatDate, formatDateTime } from "@/lib/constants";
import { Field } from "@/pages/Leads";
import { ImageUpload } from "@/components/crm/ImageUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, ShieldCheck, Check, ChevronsUpDown, Trash2, Monitor, Smartphone, LogOut, Globe, Ban, Undo2, KeyRound, AlertTriangle, Info, X, Send } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { LOGIN_OTP_SETTINGS } from "@/constants/testIds";

function BranchMultiSelect({ branches, value = [], onChange }) {
  const toggle = (id) => {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  };
  const selectedNames = branches.filter((b) => value.includes(b.id)).map((b) => b.name);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-between font-normal h-10" data-testid="branch-multi-select">
          <span className="truncate">{selectedNames.length ? selectedNames.join(", ") : "None"}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-2" align="start">
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {branches.map((b) => (
            <label key={b.id} className="flex items-center gap-2 rounded px-2 py-1.5 text-sm cursor-pointer hover:bg-accent">
              <Checkbox checked={value.includes(b.id)} onCheckedChange={() => toggle(b.id)} />
              {b.name}
            </label>
          ))}
          {branches.length === 0 && <p className="text-xs text-muted-foreground px-2 py-1">No branches found</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const ROLE_BADGE = {
  super_admin: "bg-navy text-white",
  hq_admin: "bg-gold text-white",
  branch_manager: "bg-blue-100 text-blue-800",
  franchisee_owner: "bg-purple-100 text-purple-800",
  telecaller: "bg-emerald-100 text-emerald-800",
  doctor: "bg-amber-100 text-amber-800",
};

const ROLES = ["hq_admin", "branch_manager", "franchisee_owner", "telecaller", "doctor", "receptionist"];
const DEPARTMENTS = [
  { value: "pre_sales", label: "Pre-sales" },
  { value: "post_sales", label: "Post-sales" },
  { value: "both", label: "Both" },
];
const DEPARTMENT_LABELS = { pre_sales: "Pre-sales", post_sales: "Post-sales", both: "Both" };

export default function Users() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sessionsUser, setSessionsUser] = useState(null);
  const [blacklisting, setBlacklisting] = useState(null); // user being blacklisted
  const [blacklistedOnly, setBlacklistedOnly] = useState(false);
  const [otpOpen, setOtpOpen] = useState(false);
  const canManageOtp = ["super_admin", "hq_admin"].includes(me?.role);

  const load = useCallback(() => {
    // The one caller that wants deactivated people: this is where they are
    // reactivated, so it must show them. Every other screen gets active only.
    const params = { include_inactive: true, ...(blacklistedOnly ? { blacklisted: true } : {}) };
    api.get("/users", { params })
      .then((r) => setItems(r.data)).catch(() => {});
  }, [blacklistedOnly]);
  useEffect(() => {
    load();
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, [load]);

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "User deactivated" : "User activated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    load();
  };

  const removeFromBlacklist = async (u) => {
    if (!window.confirm(`Remove "${u.name}" from the blacklist?`)) return;
    try {
      const { data } = await api.delete(`/users/${u.id}/blacklist`);
      toast.success(data?.active === false
        ? "Removed from blacklist — use the Active toggle to re-enable the account"
        : "Removed from blacklist");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete user "${u.name}"? This will deactivate the account.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-end gap-4">
        <label className="flex cursor-pointer items-center gap-2 text-sm text-ink">
          <Switch checked={blacklistedOnly} onCheckedChange={setBlacklistedOnly} data-testid="filter-blacklisted" />
          Blacklisted only
        </label>
        {canManageOtp && (
          <Button variant="outline" onClick={() => setOtpOpen(true)} data-testid={LOGIN_OTP_SETTINGS.openButton}>
            <KeyRound className="mr-2 h-4 w-4" /> Login OTP
          </Button>
        )}
        <Button onClick={() => setOpen(true)} className="bg-navy text-white hover:bg-navy-hover" data-testid="new-user-btn"><Plus className="mr-2 h-4 w-4" /> Add User</Button>
      </div>
      <TooltipProvider delayDuration={150}>
      <div className="overflow-hidden rounded-xl border border-navy/10 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-cream/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Branch</th>
              <th className="px-4 py-3">Department</th>
              <th className="px-4 py-3">Languages</th>
              <th className="px-4 py-3">Joined</th>
              <th className="px-4 py-3">Active</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-muted-foreground">{blacklistedOnly ? "No blacklisted users" : "No users"}</td></tr>
            )}
            {items.map((u) => (
              <tr key={u.id} className="border-b last:border-0 hover:bg-nude/30" data-testid={`user-${u.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-navy/10 text-sm font-semibold text-navy">{u.name?.charAt(0)}</div>
                    <div>
                      <p className="flex items-center gap-2 font-semibold text-ink">
                        {u.name}
                        {u.blacklisted && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700" data-testid={`blacklisted-badge-${u.id}`}>Blacklisted</span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="font-semibold">Reason</p>
                              <p className="whitespace-pre-wrap">{u.blacklist_reason || "—"}</p>
                              <p className="mt-1 text-xs opacity-80">By {u.blacklisted_by_name || "—"} · {formatDateTime(u.blacklisted_at)}</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">{u.username}{u.mobile && u.mobile !== u.username ? ` · ${u.mobile}` : ""}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${ROLE_BADGE[u.role] || "bg-gray-100"}`}>{ROLE_LABELS[u.role] || u.role}</span></td>
                <td className="px-4 py-3 text-muted-foreground text-xs">{(u.branch_ids || []).map((bid) => branches.find((b) => b.id === bid)?.name).filter(Boolean).join(", ") || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{DEPARTMENT_LABELS[u.department] || u.department || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{(u.languages || []).join(", ") || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{formatDate(u.created_at)}</td>
                <td className="px-4 py-3">
                  {u.blacklisted ? (
                    <Tooltip>
                      {/* span wrapper: a disabled switch swallows hover events */}
                      <TooltipTrigger asChild>
                        <span className="inline-flex"><Switch checked={false} disabled data-testid={`toggle-user-${u.id}`} /></span>
                      </TooltipTrigger>
                      <TooltipContent>Blacklisted users can't be activated — remove them from the blacklist first</TooltipContent>
                    </Tooltip>
                  ) : (
                    <Switch checked={u.active !== false} onCheckedChange={() => toggleActive(u)} data-testid={`toggle-user-${u.id}`} disabled={u.role === "super_admin"} />
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <Button variant="outline" size="sm" onClick={() => setSessionsUser(u)} data-testid={`devices-user-${u.id}`} title="Logged-in devices">
                      <Monitor className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setEditing(u)} data-testid={`edit-user-${u.id}`} disabled={u.role === "super_admin"}>Edit</Button>
                    {u.blacklisted ? (
                      <Button variant="outline" size="sm" onClick={() => removeFromBlacklist(u)} data-testid={`unblacklist-user-${u.id}`} disabled={u.role === "super_admin"}>
                        <Undo2 className="mr-1 h-3.5 w-3.5" /> Remove from blacklist
                      </Button>
                    ) : u.id !== me?.id && u.role !== "super_admin" && (
                      <Button variant="outline" size="sm" onClick={() => setBlacklisting(u)} className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`blacklist-user-${u.id}`}>
                        <Ban className="mr-1 h-3.5 w-3.5" /> Blacklist
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => deleteUser(u)} className="text-red-500 hover:bg-red-50 hover:text-red-600" data-testid={`delete-user-${u.id}`} disabled={u.role === "super_admin"}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </TooltipProvider>
      <CreateUser open={open} onOpenChange={setOpen} branches={branches} onDone={load} />
      <EditUser user={editing} onClose={() => setEditing(null)} branches={branches} onDone={load} />
      <SessionsDialog user={sessionsUser} onClose={() => setSessionsUser(null)} onChanged={load} />
      <BlacklistDialog user={blacklisting} onClose={() => setBlacklisting(null)} onDone={load} />
      {canManageOtp && <LoginOtpSettingsDialog open={otpOpen} onClose={() => setOtpOpen(false)} />}
    </div>
  );
}

// The editable part of GET/PUT /auth/otp-settings (the rest is status/audit).
const otpForm = (v) => ({
  enabled: !!v.enabled,
  channels: { whatsapp: !!v.channels?.whatsapp, email: !!v.channels?.email },
  default_working_hours: v.default_working_hours || "",
  // Absent on settings saved before this option existed — the server default is on.
  logout_after_hours: v.logout_after_hours !== false,
  whatsapp_template: v.whatsapp_template || "",
  whatsapp_approval_template: v.whatsapp_approval_template || "",
  whatsapp_language: v.whatsapp_language || "",
});

// Login OTP settings (super_admin / hq_admin): a code after the password, sent
// to the user in working hours and to super admins outside them.
function LoginOtpSettingsDialog({ open, onClose }) {
  const [view, setView] = useState(null);   // last server view, incl. status
  const [form, setForm] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setChannel = (k, v) => setForm((f) => ({ ...f, channels: { ...f.channels, [k]: v } }));

  useEffect(() => {
    if (!open) return;
    setView(null); setForm(null); setLoadError(""); setTestResults(null);
    api.get("/auth/otp-settings")
      .then((r) => { setView(r.data); setForm(otpForm(r.data)); })
      .catch((e) => setLoadError(formatApiError(e.response?.data?.detail)));
  }, [open]);

  const dirty = !!(view && form) && JSON.stringify(form) !== JSON.stringify(otpForm(view));

  const save = async () => {
    if (form.enabled && !form.channels.whatsapp && !form.channels.email) {
      toast.error("Turn on at least one channel (WhatsApp or email)."); return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/auth/otp-settings", form);
      setView(data); setForm(otpForm(data));
      toast.success("Login OTP settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true); setTestResults(null);
    try {
      const { data } = await api.post("/auth/otp-settings/test");
      setTestResults(data?.results || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  const status = view?.status || {};
  const notes = [];
  if (form) {
    if (status.kill_switch) notes.push({ bad: true, text: "LOGIN_OTP_DISABLED is set on the server — no login asks for a code, whatever is set here." });
    if (form.channels.whatsapp) {
      notes.push(status.default_whatsapp
        ? { text: `WhatsApp codes are sent from ${status.default_whatsapp.title || "the default Jalpi number"}${status.default_whatsapp.number ? ` (${status.default_whatsapp.number})` : ""}.` }
        : { bad: true, text: "No default Jalpi number — WhatsApp codes can't be sent. Set one in Integrations → Jalpi." });
    }
    if (form.channels.email && !status.smtp_configured) notes.push({ bad: true, text: "SMTP is not configured — email codes can't be sent." });
    if (status.users_unreachable) {
      notes.push({ bad: true, text: `${status.users_unreachable} active user${status.users_unreachable === 1 ? "" : "s"} can't get their own code on the enabled channels — in working hours their code goes to the super admin instead. Add a mobile or email in Edit User.` });
    }
    if (form.channels.whatsapp && status.users_without_mobile && status.users_without_mobile !== status.users_unreachable) {
      notes.push({ text: `${status.users_without_mobile} active user${status.users_without_mobile === 1 ? " has" : "s have"} no mobile number, so no WhatsApp code (email only).` });
    }
    notes.push(status.approvers_reachable
      ? { text: `${status.approvers_reachable} of ${status.approvers ?? status.approvers_reachable} super admins can receive after-hours codes.` }
      : { bad: true, text: "No super admin can receive a code on the selected channels — after hours nobody could sign in. Add a mobile/email to one, or check SMTP." });
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-navy">Login OTP</DialogTitle></DialogHeader>
        {!form ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{loadError || "Loading…"}</p>
        ) : (
          <div className="space-y-5 py-1">
            <label className="flex cursor-pointer items-start justify-between gap-4">
              <span>
                <span className="block text-sm font-semibold text-ink">Sign in with OTP (no password)</span>
                <span className="block text-xs text-muted-foreground">Staff sign in with their mobile number or email and a one-time code. Password sign-in is turned off while this is on. Older app versions can't sign in until updated.</span>
              </span>
              <Switch checked={form.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid={LOGIN_OTP_SETTINGS.enabledSwitch} />
            </label>

            <Field label="Send codes by">
              <div className="flex items-center gap-6">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-ink">
                  <Switch checked={form.channels.whatsapp} onCheckedChange={(v) => setChannel("whatsapp", v)} data-testid={LOGIN_OTP_SETTINGS.whatsappSwitch} />
                  WhatsApp
                </label>
                <label className="flex cursor-pointer items-center gap-2 text-sm text-ink">
                  <Switch checked={form.channels.email} onCheckedChange={(v) => setChannel("email", v)} data-testid={LOGIN_OTP_SETTINGS.emailSwitch} />
                  Email
                </label>
              </div>
              <p className="text-xs text-muted-foreground">Codes go to every channel the user has: WhatsApp to their mobile (profile mobile, or a phone-number username) AND email to their email (or email username).</p>
              <p className="rounded-lg bg-cream/60 px-3 py-2 text-xs text-ink">Outside working hours the code goes to all super admins, not the user — they have to ask a super admin for it.</p>
            </Field>

            <Field label="Default working hours">
              <Input value={form.default_working_hours} onChange={(e) => set("default_working_hours", e.target.value)} placeholder="9:00 AM - 8:00 PM" data-testid={LOGIN_OTP_SETTINGS.workingHoursInput} />
              <p className="text-xs text-muted-foreground">Used for users with no branch, or a branch whose hours can't be read. Per-branch hours come from Branches.</p>
            </Field>

            <label className="flex cursor-pointer items-start justify-between gap-4">
              <span>
                <span className="block text-sm font-semibold text-ink">Sign everyone out at closing time</span>
                <span className="block text-xs text-muted-foreground">When a user's branch closes, their web and app sessions end, so working after hours needs a code from a super admin. Someone already signed in with an after-hours code stays signed in until the next closing time. Super admins are never signed out (HQ admins are, like other staff).</span>
              </span>
              <Switch checked={form.logout_after_hours} onCheckedChange={(v) => set("logout_after_hours", v)} data-testid={LOGIN_OTP_SETTINGS.logoutAfterHoursSwitch} />
            </label>

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Field label="WhatsApp template">
                  <Input value={form.whatsapp_template} onChange={(e) => set("whatsapp_template", e.target.value)} placeholder="login_otp" data-testid={LOGIN_OTP_SETTINGS.templateInput} />
                </Field>
              </div>
              <Field label="Language">
                <Input value={form.whatsapp_language} onChange={(e) => set("whatsapp_language", e.target.value)} placeholder="en" data-testid={LOGIN_OTP_SETTINGS.languageInput} />
              </Field>
            </div>
            <p className="-mt-3 text-xs text-muted-foreground">Approved template on the default Jalpi number; body {"{{1}}"} = code.</p>

            <Field label="After-hours approval template (optional)">
              <Input value={form.whatsapp_approval_template} onChange={(e) => set("whatsapp_approval_template", e.target.value)} placeholder="Same as the OTP template" data-testid={LOGIN_OTP_SETTINGS.approvalTemplateInput} />
              <p className="text-xs text-muted-foreground">The message super admins get after hours. Params: {"{{1}}"} name, {"{{2}}"} branch, {"{{3}}"} code. Blank = the OTP template above is sent to them.</p>
            </Field>

            <div className="space-y-1.5" data-testid={LOGIN_OTP_SETTINGS.status}>
              {notes.map((n) => (
                <div key={n.text} className={`flex gap-2 rounded-lg px-3 py-2 text-xs ${n.bad ? "bg-amber-50 text-amber-800" : "bg-nude/20 text-ink"}`}>
                  {n.bad ? <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" /> : <Info className="mt-px h-3.5 w-3.5 shrink-0" />}
                  <span>{n.text}</span>
                </div>
              ))}
            </div>

            <div className="rounded-lg border border-navy/10 p-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-muted-foreground">{dirty ? "Save first — the test uses the saved settings." : "Sends a sample code to your own WhatsApp and email — every enabled channel, so each can be checked."}</p>
                <Button variant="outline" size="sm" onClick={sendTest} disabled={testing || dirty} data-testid={LOGIN_OTP_SETTINGS.testButton}>
                  <Send className="mr-1.5 h-3.5 w-3.5" /> {testing ? "Sending…" : "Send test code to me"}
                </Button>
              </div>
              {testResults && (
                <div className="mt-2 space-y-1" data-testid={LOGIN_OTP_SETTINGS.testResults}>
                  {testResults.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      {r.ok ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <X className="h-3.5 w-3.5 text-red-600" />}
                      <span className="font-semibold capitalize text-ink">{r.channel}</span>
                      <span className="text-muted-foreground">{r.to}</span>
                      {!r.ok && <span className="text-red-600">— {r.error || "failed"}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {view?.updated_at && <p className="text-xs text-muted-foreground">Last changed {formatDateTime(view.updated_at)}</p>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={save} disabled={!form || !dirty || saving} className="bg-navy text-white hover:bg-navy-hover" data-testid={LOGIN_OTP_SETTINGS.saveButton}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Blacklist a staff account: deactivates it and blocks login until removed.
// POST /users/:id/blacklist {reason} — the reason is required.
function BlacklistDialog({ user, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (user) setReason(""); }, [user]);

  const submit = async () => {
    if (!reason.trim()) { toast.error("Enter a reason"); return; }
    setSaving(true);
    try {
      await api.post(`/users/${user.id}/blacklist`, { reason: reason.trim() });
      toast.success(`${user.name} blacklisted`);
      onClose();
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display text-navy">Blacklist {user?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-muted-foreground">
            The account is deactivated and signed out everywhere, and can't log in or be re-activated until it's removed from the blacklist.
          </p>
          <Field label="Reason *">
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Why is this user being blacklisted?" data-testid="blacklist-reason" />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !reason.trim()} className="bg-red-600 text-white hover:bg-red-700" data-testid="blacklist-confirm">
            {saving ? "Blacklisting…" : "Blacklist"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateUser({ open, onOpenChange, branches, onDone }) {
  const empty = { name: "", username: "", password: "", email: "", mobile: "", role: "telecaller", department: "pre_sales", branch_ids: [], languages: "", max_sessions: 0 };
  const [form, setForm] = useState(empty);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.name || !form.username || !form.password) { toast.error("Name, username and password required"); return; }
    try {
      await api.post("/users", {
        ...form,
        max_sessions: Number(form.max_sessions) || 0,
        languages: form.languages ? form.languages.split(",").map((s) => s.trim()).filter(Boolean) : [],
      });
      toast.success("User created"); setForm(empty); onOpenChange(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-navy">Add User</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <Field label="Name *"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="user-name" /></Field>
          <Field label="Username *"><Input value={form.username} onChange={(e) => set("username", e.target.value.replace(/\s/g, ""))} placeholder="e.g. email, phone, or any ID (no spaces)" data-testid="user-username" /></Field>
          <Field label="Password *"><Input type="password" value={form.password} onChange={(e) => set("password", e.target.value.replace(/\s/g, ""))} data-testid="user-password" /></Field>
          <Field label="Mobile (for WhatsApp login codes)"><Input type="tel" inputMode="tel" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="10-digit mobile" data-testid="user-mobile" /></Field>
          <Field label="Email (for password reset)"><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="user@example.com" data-testid="user-email" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Role">
              <Select value={form.role} onValueChange={(v) => set("role", v)}><SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent></Select>
            </Field>
            <Field label="Department">
              <Select value={form.department} onValueChange={(v) => set("department", v)}><SelectTrigger data-testid="user-department"><SelectValue /></SelectTrigger>
                <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent></Select>
            </Field>
          </div>
          <Field label="Branches">
            <BranchMultiSelect branches={branches} value={form.branch_ids} onChange={(v) => set("branch_ids", v)} />
          </Field>
          <Field label="Languages (comma separated)"><Input value={form.languages} onChange={(e) => set("languages", e.target.value)} placeholder="English, Hindi" /></Field>
          <Field label="Max Devices (0 = unlimited)">
            <Input type="number" min={0} value={form.max_sessions} onChange={(e) => set("max_sessions", e.target.value)} data-testid="user-max-sessions" />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-navy text-white hover:bg-navy-hover" data-testid="save-user-btn">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditUser({ user, onClose, branches, onDone }) {
  const { user: me } = useAuth();
  const [form, setForm] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        username: user.username || "",
        password: "",
        confirm_password: "",
        email: user.email || "",
        mobile: user.mobile || "",
        role: user.role || "telecaller",
        department: user.department || "both",
        branch_ids: user.branch_ids || [],
        languages: (user.languages || []).join(", "),
        max_sessions: user.max_sessions ?? 0,
        qualification: user.qualification || "",
        specialization: user.specialization || "",
        registration_no: user.registration_no || "",
      });
    }
  }, [user]);
  if (!user || !form) return null;
  // Mirrors the backend: a doctor's printed credentials and signature are set
  // by the doctor themselves or by HQ — not by a branch manager.
  const canEditDoctor = me && (["super_admin", "hq_admin"].includes(me.role) || me.id === user.id);
  // A username or password change is sensitive: the admin must re-enter their
  // OWN password to confirm (matches the backend re-auth guard).
  const usernameChanged = form.username !== (user.username || "");
  const credentialChange = !!form.password || usernameChanged;
  const changedWhat = [usernameChanged && "the username", form.password && "the password"].filter(Boolean).join(" & ");
  const submit = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    if (!form.username) { toast.error("Username is required"); return; }
    if (credentialChange && !form.confirm_password) {
      toast.error("Enter your own password to confirm this change"); return;
    }
    try {
      await api.put(`/users/${user.id}`, {
        name: form.name,
        username: form.username,
        password: form.password || undefined,
        confirm_password: credentialChange ? form.confirm_password : undefined,
        email: form.email || "",
        mobile: form.mobile || "",
        role: form.role,
        department: form.department,
        branch_ids: form.branch_ids,
        languages: form.languages ? form.languages.split(",").map((s) => s.trim()).filter(Boolean) : [],
        max_sessions: Number(form.max_sessions) || 0,
      });
      if (form.role === "doctor" && canEditDoctor) {
        await api.put(`/users/${user.id}/doctor-profile`, {
          qualification: form.qualification.trim(),
          specialization: form.specialization.trim(),
          registration_no: form.registration_no.trim(),
        });
      }
      toast.success("User updated"); onClose(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-navy">Edit User</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <Field label="Name *"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="edit-user-name" /></Field>
          <Field label="Username *"><Input value={form.username} onChange={(e) => set("username", e.target.value.replace(/\s/g, ""))} placeholder="e.g. email, phone, or any ID (no spaces)" data-testid="edit-user-username" /></Field>
          <Field label="Change Password">
            <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value.replace(/\s/g, ""))} placeholder="Leave blank to keep" data-testid="edit-user-password" />
            {form.password && <p className="mt-1 text-xs text-amber-600">Changing the password will log this user out of all devices.</p>}
          </Field>
          {credentialChange && (
            <Field label="Confirm with your password *">
              <Input type="password" value={form.confirm_password} onChange={(e) => set("confirm_password", e.target.value)} placeholder="Your own login password" data-testid="edit-user-confirm-password" autoComplete="off" />
              <p className="mt-1 text-xs text-muted-foreground">Required to change {changedWhat}.</p>
            </Field>
          )}
          <Field label="Mobile (for WhatsApp login codes)"><Input type="tel" inputMode="tel" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="10-digit mobile" data-testid="edit-user-mobile" /></Field>
          <Field label="Email (for password reset)"><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="user@example.com" data-testid="edit-user-email" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Role">
              <Select value={form.role} onValueChange={(v) => set("role", v)}><SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent></Select>
            </Field>
            <Field label="Department">
              <Select value={form.department} onValueChange={(v) => set("department", v)}><SelectTrigger data-testid="edit-user-department"><SelectValue /></SelectTrigger>
                <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent></Select>
            </Field>
          </div>
          <Field label="Branches">
            <BranchMultiSelect branches={branches} value={form.branch_ids} onChange={(v) => set("branch_ids", v)} />
          </Field>
          <Field label="Languages (comma separated)"><Input value={form.languages} onChange={(e) => set("languages", e.target.value)} placeholder="English, Hindi" /></Field>
          <Field label="Max Devices (0 = unlimited)">
            <Input type="number" min={0} value={form.max_sessions} onChange={(e) => set("max_sessions", e.target.value)} data-testid="edit-user-max-sessions" />
          </Field>
          {form.role === "doctor" && (
            <div className="space-y-3 rounded-lg border border-navy/10 bg-cream/30 p-3">
              <div>
                <p className="text-sm font-semibold text-navy">Printed on prescriptions &amp; invoices</p>
                {!canEditDoctor && (
                  <p className="mt-0.5 text-xs text-muted-foreground">Only the doctor or an HQ admin can change these.</p>
                )}
              </div>
              <Field label="Qualification"><Input value={form.qualification} disabled={!canEditDoctor} onChange={(e) => set("qualification", e.target.value)} placeholder="MBBS, MD (Dermatology)" data-testid="edit-user-qualification" /></Field>
              <Field label="Specialization"><Input value={form.specialization} disabled={!canEditDoctor} onChange={(e) => set("specialization", e.target.value)} placeholder="Cosmetologist & Trichologist" data-testid="edit-user-specialization" /></Field>
              <Field label="Registration No."><Input value={form.registration_no} disabled={!canEditDoctor} onChange={(e) => set("registration_no", e.target.value)} placeholder="Medical council registration" data-testid="edit-user-registration" /></Field>
              {canEditDoctor && (
                <Field label="Signature">
                  <ImageUpload fetchUrl={`/users/${user.id}/signature`} uploadUrl={`/users/${user.id}/signature`}
                               label="Signature" hint="Sign on white paper and photograph it. PNG or JPG, under 2 MB."
                               testId="edit-user-signature" />
                </Field>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="bg-navy text-white hover:bg-navy-hover" data-testid="update-user-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Admin view of a user's logged-in devices (sessions): list active sessions with
// a NEW badge for unrecognised devices, log any of them out (or all), set the
// device limit, and see recent login activity.
function SessionsDialog({ user, onClose, onChanged }) {
  const [data, setData] = useState(null);      // { active, revoked, max_sessions }
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    if (!user) return;
    setLoading(true);
    Promise.all([
      api.get(`/users/${user.id}/sessions`).then((r) => setData(r.data)).catch(() => setData({ active: [], revoked: [], max_sessions: 0 })),
      api.get(`/users/${user.id}/login-activity`).then((r) => setActivity(r.data)).catch(() => setActivity([])),
    ]).finally(() => setLoading(false));
  }, [user]);
  useEffect(() => { load(); }, [load]);

  if (!user) return null;

  const revokeOne = async (sid) => {
    setBusyId(sid);
    try {
      await api.delete(`/users/${user.id}/sessions/${sid}`);
      toast.success("Device logged out");
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const revokeAll = async () => {
    if (!window.confirm(`Log ${user.name} out of all devices?`)) return;
    try {
      const { data: res } = await api.post(`/users/${user.id}/sessions/revoke-all`);
      toast.success(`Logged out ${res.revoked} device(s)`);
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const active = data?.active || [];
  const DeviceIcon = (p) => (p === "android" || p === "ios") ? Smartphone : (p === "web" ? Globe : Monitor);

  return (
    <Dialog open={!!user} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">Devices — {user.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {active.length} active device{active.length === 1 ? "" : "s"}
              {data?.max_sessions ? ` · limit ${data.max_sessions}` : " · no limit"}
            </span>
            {active.length > 0 && (
              <Button variant="outline" size="sm" onClick={revokeAll} className="text-red-500 hover:bg-red-50 hover:text-red-600" data-testid="logout-all-btn">
                <LogOut className="mr-1.5 h-3.5 w-3.5" /> Log out all
              </Button>
            )}
          </div>

          {loading && !data ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
          ) : active.length === 0 ? (
            <p className="rounded-lg border border-dashed border-navy/15 py-6 text-center text-sm text-muted-foreground">No active devices.</p>
          ) : (
            <div className="space-y-2">
              {active.map((s) => {
                const Icon = DeviceIcon(s.platform);
                return (
                  <div key={s.id} className="flex items-center gap-3 rounded-lg border border-navy/10 p-3" data-testid={`session-${s.id}`}>
                    <Icon className="h-5 w-5 shrink-0 text-navy" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="truncate text-sm font-semibold text-ink">{s.device || "Unknown device"}</p>
                        {s.is_new_device && <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">NEW</span>}
                      </div>
                      <p className="truncate text-xs text-muted-foreground">
                        {s.ip || "—"} · last active {formatDateTime(s.last_seen)}
                      </p>
                    </div>
                    <Button variant="outline" size="sm" disabled={busyId === s.id} onClick={() => revokeOne(s.id)}
                      className="text-red-500 hover:bg-red-50 hover:text-red-600" data-testid={`logout-session-${s.id}`}>
                      <LogOut className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Recent login activity */}
          {activity.length > 0 && (
            <div>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Recent login activity</p>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {activity.map((a) => (
                  <div key={a.id} className="flex items-center justify-between rounded bg-cream/40 px-2.5 py-1.5 text-xs">
                    <span className={`font-medium ${a.action === "login" ? "text-emerald-700" : "text-navy"}`}>
                      {a.action === "login" ? "Logged in" : "Logged out"}
                    </span>
                    <span className="text-muted-foreground">{formatDateTime(a.created_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
